import { Component, ViewChild } from '@angular/core';
import * as moment from 'moment';
import { Observable } from 'rxjs';
import { NgForm } from '@angular/forms';
import { Store, select } from '@ngrx/store';
import { getCustomHours } from '../store/selectors/calendars.selectors';
import { CalendarsOperations } from '../store/reducers/calendars-operation';
import { AddWorkingTimeSchema, DeleteWorkingTimeSchemaRange, ModifyWorkingTimeSchema } from '../store/actions/calendars.actions';
import { GenomeTimeService } from 'app/shared/services/genome-time.service';
import { IGenomeTime } from 'bz-genome-angular/time-picker/time-picker/bzg-time-picker.component';
import { IAppState } from 'app/IAppState';
import { IFilterColumn } from 'app/scheduler/jobs/IFilterColumn';

interface ITimeRange {
  fromTime: IGenomeTime,
  toTime: IGenomeTime
}

@Component({
  selector: 'working-time-schema-hours',
  templateUrl: 'working-time-schema-hours.component.html',
  styleUrls: ['working-time-schema-hours.component.scss']
})

export class WorkingTimeSchemaHoursComponent {
  public customWorkingHours$: Observable<IWorkingTimeSchema[]>;
  public currentDateType: string;
  public startDate: Date = new Date();
  public finishDate: Date = new Date();
  public onDate: Date = new Date();
  public selectedSchemaRange: IWorkingTimeSchema;
  public timeList: Array<ITimeRange> = [];

  @ViewChild('workingHoursForm', { static: true }) workingHoursForm: NgForm;

  constructor(private _store: Store<IAppState>, private _genomeTimeService: GenomeTimeService) {
    this.customWorkingHours$ = this._store.pipe(select(getCustomHours));
    this._resetDates();
  }

  public columns: Array<IFilterColumn> = [
    { key: 'fromDate', value: 'WORKING_TIME_SCHEMA.COLUMN_START_DATE', visible: true, type: 'date' },
    { key: 'toDate', value: 'WORKING_TIME_SCHEMA.COLUMN_FINISH_DATE', visible: true, type: 'date' },
    { key: 'days', value: 'WORKING_TIME_SCHEMA.COLUMN_DAYS', visible: true, type: 'number' },
    { key: 'timeDetails', value: 'WORKING_TIME_SCHEMA.TIME_DETAILS', visible: true, type: 'detail' },
    { key: 'edit', value: 'WORKING_TIME_SCHEMA.COLUMN_EDIT', visible: true, type: 'edit' },
    { key: 'delete', value: 'WORKING_TIME_SCHEMA.COLUMN_DELETE', visible: true, type: 'delete' },
  ];

  public isInvalid() {
    if (this.startDate) {
      this.startDate.setHours(0, 0, 0, 0);
    }

    if (this.finishDate) {
      this.finishDate.setHours(0, 0, 0, 0);
    }

    return this.workingHoursForm.invalid || (this.currentDateType === 'range' && this.startDate > this.finishDate);
  }

  public onSetCustomWorkingHours() {
    if (this.isInvalid()) {
      console.log('Not Saved');
    } else {
      const newWorkingTimeSchema = CalendarsOperations.getEmptyWorkingTimeSchema(true);
      if (this.currentDateType === 'exact') {
        newWorkingTimeSchema.fromDate = CalendarsOperations.getStringDate(this.onDate);
        newWorkingTimeSchema.toDate = CalendarsOperations.getStringDate(this.onDate);
      } else {
        newWorkingTimeSchema.fromDate = CalendarsOperations.getStringDate(this.startDate);
        newWorkingTimeSchema.toDate = CalendarsOperations.getStringDate(this.finishDate);
      }
      for (let i = 1; i <= this.timeList.length; i++) {
        newWorkingTimeSchema['fromTime' + i] = this._getStringTimeFormat(this.timeList[i - 1].fromTime);
        newWorkingTimeSchema['toTime' + i] = this._getStringTimeFormat(this.timeList[i - 1].toTime);
      }
      this._store.dispatch(new AddWorkingTimeSchema(newWorkingTimeSchema));
      this._resetDates();
    }
  }
  public getDaysBetween(from: string, to: string) {
    const start = moment(from);
    const end = moment(to);
    return end.diff(start, 'days') + 1;
  }

  public closePopover(popover) {
    popover.hide();
  }

  public addTimeItem() {
    this.timeList.push({
      fromTime: this._getEmptyTime(),
      toTime: this._getEmptyTime()
    });
  }

  public removeTimeItem(index: number) {
    this.timeList.splice(index, 1);
  }

  private _resetDates() {
    this.currentDateType = 'exact';
    this.startDate = new Date();
    this.finishDate = new Date();
    this.onDate = new Date();
    this.timeList = [];
    this.addTimeItem();
  }


  private _getEmptyTime() {
    return {
      hours: 12,
      minutes: 0,
      seconds: 0,
      period: 'AM'
    };
  }

  public _getStringTimeFormat(time: IGenomeTime): string {
    const date = new Date();
    let militaryHours: number;

    if (time.period === 'PM' && time.hours < 12) {
      militaryHours = time.hours + 12;
    }
    else if (time.period === 'AM' && time.hours === 12) {
      militaryHours = time.hours - 12;
    }
    else {
      militaryHours = time.hours;
    }
    date.setHours(militaryHours, time.minutes, time.seconds, 0);
    return CalendarsOperations.getStringTimestamp(date);
  }

  public deleteWorkingTimeSchemaRange(workingTimeSchemaRange: IWorkingTimeSchema) {
    this._store.dispatch(new DeleteWorkingTimeSchemaRange(workingTimeSchemaRange));
  }

  public selectSchemaRange(schema: IWorkingTimeSchema) {
    this.selectedSchemaRange = schema;
    if (schema.fromDate === schema.toDate) {
      this.currentDateType = 'exact';
    } else {
      this.currentDateType = 'range';
    }
    this.onDate = moment(schema.fromDate).toDate();
    this.startDate = moment(schema.fromDate).toDate();
    this.finishDate = moment(schema.toDate).toDate();
    this.timeList = [];
    for (let i = 1; i <= 5; i++) {
      if (schema['fromTime' + i] && schema['toTime' + i]) {
        this.timeList.push({
          fromTime: this._genomeTimeService.getTimeFromDate(new Date(schema['fromTime' + i])),
          toTime: this._genomeTimeService.getTimeFromDate(new Date(schema['toTime' + i])),
        })
      }
    }
  }

  public modifySchemaRange(schema: IWorkingTimeSchema) {
    if (this.currentDateType === 'exact') {
      schema.fromDate = CalendarsOperations.getStringDate(this.onDate);
      schema.toDate = CalendarsOperations.getStringDate(this.onDate);
    } else {
      schema.fromDate = CalendarsOperations.getStringDate(this.startDate);
      schema.toDate = CalendarsOperations.getStringDate(this.finishDate);
    }
    for (let i = 1; i <= 5; i++) {
      schema['fromTime' + i] = null;
      schema['toTime' + i] = null;
    }
    for (let i = 1; i <= this.timeList.length; i++) {
      schema['fromTime' + i] = this._getStringTimeFormat(this.timeList[i - 1].fromTime);
      schema['toTime' + i] = this._getStringTimeFormat(this.timeList[i - 1].toTime);
    }
    this._store.dispatch(new ModifyWorkingTimeSchema(schema));
    this.startDate = new Date();
    this.finishDate = new Date();
    this.selectedSchemaRange = undefined;
  }

  public cancelModify() {
    this.selectedSchemaRange = undefined;
    this._resetDates();
  }
}
